import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const MIN_PERCENT = 20;
const MAX_PERCENT = 80;
const COLLAPSED_PX = 68;
const EXPAND_THRESHOLD_PX = 100;
const DRAG_MOVE_THRESHOLD_PX = 4;

function pxToPercent(px, containerWidth) {
  if (!containerWidth) return 0;
  return (px / containerWidth) * 100;
}

function applyLeftPanelWidth(leftPanel, container, newWidthPx, isBootstrapGrid) {
  if (isBootstrapGrid) {
    container.style.setProperty("--left-panel-width", `${newWidthPx}px`);
    return;
  }
  const containerWidth = container.getBoundingClientRect().width;
  const newPercent = pxToPercent(newWidthPx, containerWidth);
  leftPanel.style.flex = `0 0 ${newPercent}%`;
  leftPanel.style.maxWidth = `${newPercent}%`;
}

function finalizeBootstrapPanel(leftPanel, container, didDrag, lastWidthPx) {
  if (!didDrag) {
    leftPanel.classList.remove("left-resizing");
    if (!leftPanel.classList.contains("left-expanded")) {
      container.style.removeProperty("--left-panel-width");
    }
    return;
  }

  if (lastWidthPx == null) {
    leftPanel.classList.remove("left-resizing");
    return;
  }

  if (lastWidthPx <= EXPAND_THRESHOLD_PX) {
    leftPanel.classList.remove("left-resizing");
    container.style.removeProperty("--left-panel-width");
    leftPanel.classList.remove("left-expanded");
    window.dispatchEvent(new CustomEvent("left-panel-collapsed"));
    return;
  }

  container.style.setProperty("--left-panel-width", `${lastWidthPx}px`);
  window.dispatchEvent(
    new CustomEvent("left-panel-width-changed", { detail: { widthPx: lastWidthPx } })
  );

  if (!leftPanel.classList.contains("left-expanded")) {
    leftPanel.classList.add("left-expanded");
    window.dispatchEvent(new CustomEvent("left-panel-expanded"));
  }

  leftPanel.classList.remove("left-resizing");
}

function createDivider() {
  const divider = document.createElement("div");
  divider.className = "resize-divider";
  divider.setAttribute("aria-hidden", "true");

  const handle = document.createElement("div");
  handle.className = "resize-divider-handle";
  divider.appendChild(handle);

  return divider;
}

function addResizeLogic(divider, leftPanel, rightPanel, container, isBootstrapGrid) {
  let isDragging = false;
  let didDrag = false;
  let resizingFromCollapsed = false;
  let startX = 0;
  let startWidth = 0;
  let containerWidth = 0;
  let pendingX = null;
  let rafId = null;
  let lastWidthPx = null;

  const applyWidthFromX = (clientX) => {
    const diff = clientX - startX;
    if (Math.abs(diff) >= DRAG_MOVE_THRESHOLD_PX) {
      didDrag = true;
    }

    let newWidth = startWidth + diff;

    const minPx = resizingFromCollapsed
      ? COLLAPSED_PX
      : (MIN_PERCENT / 100) * containerWidth;
    const maxPx = (MAX_PERCENT / 100) * containerWidth;
    newWidth = Math.max(minPx, Math.min(maxPx, newWidth));

    lastWidthPx = newWidth;
    applyLeftPanelWidth(leftPanel, container, newWidth, isBootstrapGrid);
  };

  const applyWidth = () => {
    rafId = null;
    if (pendingX === null) return;
    applyWidthFromX(pendingX);
    pendingX = null;
  };

  const onMouseDown = (e) => {
    isDragging = true;
    didDrag = false;
    resizingFromCollapsed = false;
    startX = e.clientX;
    containerWidth = container.getBoundingClientRect().width;
    startWidth = leftPanel.getBoundingClientRect().width;

    if (isBootstrapGrid && !leftPanel.classList.contains("left-expanded")) {
      resizingFromCollapsed = true;
      lastWidthPx = startWidth || COLLAPSED_PX;
      container.style.setProperty("--left-panel-width", `${lastWidthPx}px`);
      leftPanel.classList.add("left-resizing");
    }

    leftPanel.style.transition = "none";
    rightPanel.style.transition = "none";

    divider.classList.add("dragging");
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    e.preventDefault();
  };

  const onMouseMove = (e) => {
    if (!isDragging) return;
    pendingX = e.clientX;
    if (!rafId) {
      rafId = requestAnimationFrame(applyWidth);
    }
  };

  const onMouseUp = (e) => {
    if (!isDragging) return;

    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }

    if (e.clientX != null) {
      applyWidthFromX(e.clientX);
    }

    isDragging = false;
    pendingX = null;

    if (isBootstrapGrid) {
      finalizeBootstrapPanel(leftPanel, container, didDrag, lastWidthPx);
    }

    leftPanel.style.transition = "";
    rightPanel.style.transition = "";

    divider.classList.remove("dragging");
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    resizingFromCollapsed = false;
  };

  divider.addEventListener("mousedown", onMouseDown);
  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);

  return () => {
    if (rafId) cancelAnimationFrame(rafId);
    divider.removeEventListener("mousedown", onMouseDown);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };
}

function hasPendingPanels() {
  for (const commonPart of document.querySelectorAll(
    ".commonPart:not([data-resizable])"
  )) {
    const left = commonPart.querySelector(":scope > .left");
    const right = commonPart.querySelector(":scope > .right");
    if (left && right) return true;
  }

  for (const container of document.querySelectorAll(
    ".common-direction:not([data-resizable])"
  )) {
    const leftPanel = container.querySelector(".leftMobile");
    const rightPanel = container.querySelector(".right-side");
    if (!leftPanel || !rightPanel) continue;

    const containerStyle = window.getComputedStyle(container);
    if (
      containerStyle.flexDirection === "column" ||
      containerStyle.flexDirection === "column-reverse"
    ) {
      continue;
    }

    return true;
  }

  return false;
}

function initResizablePanels() {
  const allCleanups = [];

  const commonParts = document.querySelectorAll(
    ".commonPart:not([data-resizable])"
  );
  commonParts.forEach((commonPart) => {
    const left = commonPart.querySelector(":scope > .left");
    const right = commonPart.querySelector(":scope > .right");
    if (!left || !right) return;

    commonPart.setAttribute("data-resizable", "true");

    right.style.transition = "none";
    left.style.transition = "none";
    right.style.flex = "1";
    right.style.maxWidth = "none";
    requestAnimationFrame(() => {
      right.style.transition = "";
      left.style.transition = "";
    });

    const divider = createDivider();
    commonPart.insertBefore(divider, right);

    allCleanups.push(addResizeLogic(divider, left, right, commonPart, false));
  });

  const commonDirections = document.querySelectorAll(
    ".common-direction:not([data-resizable])"
  );
  commonDirections.forEach((container) => {
    const leftPanel = container.querySelector(".leftMobile");
    const rightPanel = container.querySelector(".right-side");
    if (!leftPanel || !rightPanel) return;

    const containerStyle = window.getComputedStyle(container);
    if (
      containerStyle.flexDirection === "column" ||
      containerStyle.flexDirection === "column-reverse"
    ) {
      return;
    }

    container.setAttribute("data-resizable", "true");
    container.style.display = "flex";
    container.style.flexWrap = "nowrap";
    container.style.alignItems = "stretch";

    rightPanel.style.transition = "none";
    leftPanel.style.transition = "none";
    rightPanel.style.flex = "1";
    rightPanel.style.maxWidth = "none";
    rightPanel.style.width = "auto";
    requestAnimationFrame(() => {
      rightPanel.style.transition = "";
      leftPanel.style.transition = "";
    });

    const divider = createDivider();
    container.insertBefore(divider, rightPanel);

    allCleanups.push(
      addResizeLogic(divider, leftPanel, rightPanel, container, true)
    );
  });

  return allCleanups;
}

function teardownResizablePanels(listenerCleanups) {
  listenerCleanups.forEach((fn) => fn());
  listenerCleanups.length = 0;
  document.querySelectorAll(".resize-divider").forEach((d) => d.remove());
  document.querySelectorAll("[data-resizable]").forEach((el) => {
    el.removeAttribute("data-resizable");
  });
}

const ResizableLayout = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.innerWidth <= 991) return;

    const listenerCleanups = [];
    let observer = null;
    let rafId = null;
    let initScheduled = false;

    const runInit = () => {
      if (!hasPendingPanels()) return;
      listenerCleanups.push(...initResizablePanels());
      window.dispatchEvent(new CustomEvent("resizable-panels-ready"));
    };

    const scheduleInit = () => {
      if (initScheduled) return;
      initScheduled = true;
      rafId = requestAnimationFrame(() => {
        initScheduled = false;
        rafId = null;
        runInit();
      });
    };

    teardownResizablePanels(listenerCleanups);
    scheduleInit();

    observer = new MutationObserver(scheduleInit);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer?.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
      teardownResizablePanels(listenerCleanups);
    };
  }, [location.pathname]);

  return null;
};

export default ResizableLayout;
